import {
  Avatar,
  Flex,
  HStack,
  Icon,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import UserResource from 'api/user';
import {
  activeStyles,
  normalStyles,
  separatorStyles,
} from 'assets/css/commonStyles';
import {
  Container,
  Next,
  PageGroup,
  Paginator,
  Previous,
  usePaginator,
} from 'chakra-paginator';
import CustomTagTitle from 'components/common/CustomTagTitle';
import PageLimitChanger from 'components/common/PageLimitChanger';
import ResultCountDisplay from 'components/common/ResultCountDisplay';
import TableSkeletonLoader from 'components/common/TableSkeletonLoader';
import { strings } from 'config/localization';
import { DEFAULT_PAGE_SIZE, INITIAL_CURRENT_PAGE } from 'constants/common';
import routes from 'constants/routes';
import React, { useMemo, useState } from 'react';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import { DateFormatMDY, getStartingSerialNumber } from 'utils';
import { getFullName } from 'utils/esg';

interface FilterParams {
  currentPage: number;
  pageSize: number;
}

interface Props {
  companyId: number;
  signJSAId: any;
}

const CompanyActiveCrew: React.FC<Props> = (props) => {
  const { companyId, signJSAId } = props;
  const [isLoading, setIsLoading] = useState<string | null>();
  const userAPI = new UserResource();
  const [filterParams, setFilterParams] = useState<FilterParams>({
    currentPage: INITIAL_CURRENT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [totalUsers, setTotalUsers] = useState<number | undefined>(undefined);

  const { pagesQuantity, setPageSize } = usePaginator({
    total: totalUsers,
    initialState: {
      pageSize: DEFAULT_PAGE_SIZE,
      currentPage: INITIAL_CURRENT_PAGE,
    },
  });

  const { data: userList, isLoading: isUserListLoading } = useQuery(
    [
      'companyActiveCrew',
      {
        page: filterParams.currentPage,
        limit: filterParams.pageSize,
      },
      signJSAId,
    ],
    async () => {
      const queryParams: any = {
        page: filterParams.currentPage,
        limit: filterParams.pageSize,
      };
      queryParams.company = companyId;
      const response = await userAPI.crewList(signJSAId.id, queryParams);
      setTotalUsers(response?.data?.data?.meta?.total);
      response && setIsLoading(null);
      return response?.data?.data;
    }
  );

  const handlePageChange = (nextPage: number) => {
    setFilterParams((prevState) => ({
      ...prevState,
      currentPage: nextPage,
    }));
  };

  const startingSN = useMemo(() => {
    return getStartingSerialNumber(
      filterParams.currentPage,
      filterParams.pageSize
    );
  }, [filterParams.currentPage, filterParams.pageSize]);

  return (
    <>
      <Stack direction="column" spacing="4">
        <Stack bg="white" p={['3', '6']} shadow="box" rounded="sm">
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                <Th>#</Th>
                  <Th>Company</Th>
                  <Th>{strings.name}</Th>
                  <Th>Time JSA Started</Th>
                  <Th>JSA Active Status (Yes or No)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {!isUserListLoading &&
                  userList?.results[0]?.members?.map((userData: any, index: number) => {
                    return (
                      <Tr key={userData.id}>
                        <Td>{startingSN + index}</Td>
                        <Td>{userData?.company}</Td>
                        <Td>{getFullName(userData)}</Td>
                        <Td>{DateFormatMDY(userData?.created_at)}</Td>
                        <Td>{userData?.is_active ? strings.yes : strings.no}</Td>
                      </Tr>
                    );
                  })}
                {isUserListLoading && (
                  <TableSkeletonLoader rows={filterParams.pageSize} cols={6} />
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Stack>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          overflow="auto">
          <Text color="gray.400">
            <ResultCountDisplay
              isLoading={isUserListLoading}
              totalCount={totalUsers}
              resultCount={userList?.results?.length}
              pageSize={filterParams.pageSize}
              currentPage={filterParams.currentPage}
            />
          </Text>
          <Flex alignItems="center" flexWrap={['wrap', 'nowrap']}>
            {userList?.results?.length > 0 && (
              <PageLimitChanger
                pageSize={filterParams.pageSize}
                setPageSize={(value: any) => {
                  setFilterParams((prevState) => ({
                    ...prevState,
                    currentPage: INITIAL_CURRENT_PAGE,
                    pageSize: value,
                  }));
                  setPageSize(value);
                }}
              />
            )}
            {!isUserListLoading && pagesQuantity > 1 && (
              <Paginator
                activeStyles={activeStyles}
                currentPage={filterParams.currentPage}
                normalStyles={normalStyles}
                separatorStyles={separatorStyles}
                pagesQuantity={pagesQuantity}
                onPageChange={handlePageChange}
                innerLimit={2}
                outerLimit={2}>
                <Container
                  align="center"
                  justifyContent="flex-end"
                  w="full"
                  p={1}
                  pr={0}
                  ml={3}>
                  <Previous mr="0.3rem" sx={normalStyles} minWidth={0}>
                    <Icon as={BiChevronLeft} fontSize="xl" />
                  </Previous>
                  <PageGroup isInline align="center" />
                  <Next ml="0.3rem" sx={normalStyles} minWidth={0}>
                    <Icon as={BiChevronRight} fontSize="xl" />
                  </Next>
                </Container>
              </Paginator>
            )}
          </Flex>
        </Flex>
      </Stack>
    </>
  );
};

export default CompanyActiveCrew;
